/**
 * Cart drawer enhancements
 * -------------------------------------------------------------------------------------------------------
 * Adds working -/+ stepper buttons to the cart drawer's line items.
 *
 * Why this exists: the theme's built-in <quantity-selector> element (used on the product page) updates the
 * quantity input's value on click, but it never fires a "change" event — on the product page that's fine,
 * because the quantity is only read when the Add to cart form is submitted. Inside the cart drawer, though,
 * quantity updates need to trigger the theme's existing AJAX cart update (handled by the theme's
 * <line-item-quantity> element, which listens for a bubbling "change" event on its quantity input).
 *
 * So instead of reusing <quantity-selector>, the +/- buttons here are plain buttons with
 * [data-quantity-decrease] / [data-quantity-increase] attributes. A single delegated listener on
 * `document` handles clicks, updates the input's value using the theme's own QuantityInput logic
 * (via the "quantity" property already exposed on inputs with `is="quantity-input"`), then dispatches a
 * real "change" event so the existing cart AJAX flow picks it up exactly as if the customer had typed a
 * new value.
 *
 * Event delegation on `document` is used (rather than binding to individual buttons on page load) because
 * the cart drawer's contents are replaced wholesale after every AJAX cart update — see the theme's
 * CartDrawer class, which calls `replaceChildren(...)` on the <cart-drawer> element. A delegated listener
 * keeps working after that replacement without needing any re-initialization.
 */
(function () {
  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-quantity-decrease], [data-quantity-increase]');
    if (!button) return;

    var stepper = button.closest('[data-line-item-stepper]');
    if (!stepper) return;

    var input = stepper.querySelector('input[is="quantity-input"]');
    if (!input) return;

    var currentQuantity = parseInt(input.value, 10);
    if (isNaN(currentQuantity)) currentQuantity = 1;

    var targetQuantity = button.hasAttribute('data-quantity-decrease')
      ? currentQuantity - 1
      : currentQuantity + 1;

    // Use the native setter exposed by the theme's QuantityInput class (extends HTMLInputElement) so min/max
    // bounds defined on the input (e.g. inventory limits) are respected exactly like typing would.
    if ('quantity' in input) {
      input.quantity = targetQuantity;
    } else {
      input.value = Math.max(1, targetQuantity);
    }

    // The theme's <line-item-quantity> element only reacts to a real "change" event bubbling up from the
    // input, so we dispatch one manually here.
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /**
   * Gift progress bar — "copy code" button for discount-code milestones.
   * The code and the trigger button live inside the same [data-copy-code] wrapper, so we read the code from
   * that wrapper's attribute rather than trying to parse it out of the button's own text/siblings.
   */
  document.addEventListener('click', function (event) {
    var trigger = event.target.closest('[data-copy-code-trigger]');
    if (!trigger) return;

    var wrapper = trigger.closest('[data-copy-code]');
    if (!wrapper) return;

    var code = wrapper.getAttribute('data-copy-code');
    if (!code || !navigator.clipboard) return;

    navigator.clipboard.writeText(code).then(function () {
      var originalText = trigger.textContent;
      trigger.textContent = '✓';

      setTimeout(function () {
        trigger.textContent = originalText;
      }, 1500);
    });
  });
})();
/* --- iOS Safari fix: overflow:hidden på <html> alene forhindrer ikke pålideligt at baggrunden kan
   scrolles med touch, mens en drawer/dialog er åben. Denne kode låser siden fysisk på plads og
   genskaber præcis samme scroll-position når drawer'en/dialogen lukkes igen. Gælder alle drawers/
   dialoger på sitet (kurv, søgning, navigation, quick-buy), da de alle deler samme "lock"-klasse. */
(function () {
  let savedScrollY = 0;

  const lockBody = () => {
    savedScrollY = window.scrollY || window.pageYOffset;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${savedScrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
  };

  const unlockBody = () => {
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    window.scrollTo(0, savedScrollY);
  };

  const observer = new MutationObserver(() => {
    const isLocked = document.documentElement.classList.contains('lock');
    const bodyIsFixed = document.body.style.position === 'fixed';

    if (isLocked && !bodyIsFixed) {
      lockBody();
    } else if (!isLocked && bodyIsFixed) {
      unlockBody();
    }
  });

  observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
})();

/**
 * "You may like" carousel — scroll-progress indicator
 * -------------------------------------------------------------------------------------------------------
 * Drives the thin track + sliding thumb rendered just below the recommendations carousel (markup + CSS in
 * cart-drawer.liquid / custom-style.css). Thumb width mirrors how much of the carousel is currently
 * visible (clientWidth / scrollWidth), and its position mirrors scroll progress — standard scrollbar
 * behaviour, just styled as a bold rectangle on a thin line instead of the browser's native scrollbar.
 *
 * "scroll" events don't bubble, so the only reliable way to delegate them (rather than binding directly to
 * the carousel element, which — like the quantity steppers above — gets discarded whenever the cart
 * drawer's contents are replaced after an AJAX cart update) is a capture-phase listener on `document`.
 * A MutationObserver re-measures the thumb after every such replacement, and on first render.
 */
(function () {
  function updateProgress(carousel) {
    var progress = document.querySelector('[data-carousel-progress-for="' + carousel.id + '"]');
    if (!progress) return;

    var track = progress.querySelector('.cart-drawer__recommendations-progress-track');
    var thumb = progress.querySelector('.cart-drawer__recommendations-progress-thumb');
    if (!track || !thumb) return;

    var maxScrollLeft = carousel.scrollWidth - carousel.clientWidth;

    // Nothing to scroll (all recommended products already fit on screen) — hide the indicator entirely
    // rather than show a thumb that fills the whole track and never moves.
    if (maxScrollLeft <= 1) {
      progress.style.display = 'none';
      return;
    }
    progress.style.display = '';

    var trackWidth = track.clientWidth;
    var visibleRatio = Math.min(1, carousel.clientWidth / carousel.scrollWidth);
    var thumbWidth = Math.max(trackWidth * visibleRatio, 32); // never shrinks below 32px so it stays visible
    var scrollRatio = carousel.scrollLeft / maxScrollLeft;
    var thumbLeft = (trackWidth - thumbWidth) * scrollRatio;

    thumb.style.width = thumbWidth + 'px';
    thumb.style.transform = 'translateX(' + thumbLeft + 'px)';
  }

  function updateAllProgressBars() {
    document.querySelectorAll('.cart-drawer__recommendations-carousel').forEach(updateProgress);
  }

  document.addEventListener('scroll', function (event) {
    if (!event.target.classList || !event.target.classList.contains('cart-drawer__recommendations-carousel')) return;
    updateProgress(event.target);
  }, true);

  window.addEventListener('resize', function () {
    updateAllProgressBars();
  });

  // Re-measure whenever the cart drawer's contents are (re)rendered — covers first load, opening the
  // drawer, and every AJAX cart update (add/remove/change quantity all trigger a full re-render).
  var cartDrawer = document.getElementById('cart-drawer');
  if (cartDrawer) {
    new MutationObserver(function () {
      requestAnimationFrame(updateAllProgressBars);
    }).observe(cartDrawer, { childList: true, subtree: true });
  }

  document.addEventListener('DOMContentLoaded', updateAllProgressBars);
})();

/**
 * "You may like" carousel — draggable progress thumb
 * -------------------------------------------------------------------------------------------------------
 * Lets the customer grab the progress thumb directly and drag it to scroll the carousel, instead of only
 * being able to swipe the product cards themselves. Uses Pointer Events (covers mouse + touch + pen with
 * one code path) and drives the carousel via `scrollLeft`, which fires the same native "scroll" event the
 * indicator above already listens for — so dragging and swiping stay perfectly in sync through the same
 * rendering code, with no separate "drag state" to keep visually consistent.
 *
 * `pointerdown` bubbles (unlike "scroll"), so a plain delegated listener on `document` is enough here —
 * no MutationObserver needed for this part, since we only need to find the elements at the moment the
 * drag starts, not keep a persistent reference across cart re-renders.
 */
(function () {
  var activeDrag = null;

  function findDragTargets(thumb) {
    var progress = thumb.closest('.cart-drawer__recommendations-progress');
    if (!progress) return null;

    var track = progress.querySelector('.cart-drawer__recommendations-progress-track');
    var carousel = document.getElementById(progress.getAttribute('data-carousel-progress-for'));
    if (!track || !carousel) return null;

    return { track: track, carousel: carousel };
  }

  document.addEventListener('pointerdown', function (event) {
    var thumb = event.target.closest('.cart-drawer__recommendations-progress-thumb');
    if (!thumb) return;

    var targets = findDragTargets(thumb);
    if (!targets) return;

    var maxScrollLeft = targets.carousel.scrollWidth - targets.carousel.clientWidth;
    if (maxScrollLeft <= 0) return; // nothing to drag — carousel isn't scrollable

    activeDrag = {
      pointerId: event.pointerId,
      thumb: thumb,
      carousel: targets.carousel,
      trackWidth: targets.track.clientWidth,
      thumbWidth: thumb.offsetWidth,
      maxScrollLeft: maxScrollLeft,
      startX: event.clientX,
      startScrollLeft: targets.carousel.scrollLeft,
    };

    thumb.setPointerCapture(event.pointerId);
    thumb.classList.add('is-dragging');
    // The carousel uses scroll-snap-type: x mandatory (for the normal swipe feel), which fights direct
    // scrollLeft assignment by snapping to the nearest item on every frame — this is what caused the
    // "jump to next product" behaviour. Disabling it for the duration of the drag lets the thumb move
    // freely; it's restored on release so swipe/scroll-snap behaves normally again afterwards.
    targets.carousel.style.scrollSnapType = 'none';
    event.preventDefault();
  });

  document.addEventListener('pointermove', function (event) {
    if (!activeDrag || event.pointerId !== activeDrag.pointerId) return;

    var deltaX = event.clientX - activeDrag.startX;
    var maxThumbLeft = activeDrag.trackWidth - activeDrag.thumbWidth;
    var scrollRatio = maxThumbLeft > 0 ? deltaX / maxThumbLeft : 0;
    var newScrollLeft = activeDrag.startScrollLeft + scrollRatio * activeDrag.maxScrollLeft;

    // Direct assignment (not scrollTo/smooth) so movement tracks the pointer without any easing lag.
    activeDrag.carousel.scrollLeft = Math.max(0, Math.min(activeDrag.maxScrollLeft, newScrollLeft));
  });

  function endDrag(event) {
    if (!activeDrag || event.pointerId !== activeDrag.pointerId) return;
    activeDrag.thumb.classList.remove('is-dragging');
    activeDrag.thumb.releasePointerCapture(activeDrag.pointerId);
    activeDrag.carousel.style.scrollSnapType = ''; // restore the carousel's normal swipe/snap behaviour
    activeDrag = null;
  }

  document.addEventListener('pointerup', endDrag);
  document.addEventListener('pointercancel', endDrag);
})();

/**
 * "You may like" carousel — autoplay
 * -------------------------------------------------------------------------------------------------------
 * Automatically glides to the next product every few seconds, using the carousel's own native smooth
 * scroll — the same mechanism swipe/drag already use, and the same scroll-snap that gives the "glidende"
 * settle-into-place feel seen elsewhere on the site. No separate transition/animation system to keep in
 * sync: the progress indicator built earlier already listens to the carousel's native "scroll" event
 * regardless of what caused it, so it tracks autoplay for free.
 *
 * Pauses while the customer is actively interacting (swiping the carousel, dragging the progress thumb,
 * or hovering it on desktop) and resumes a few seconds after they stop. Only runs while the carousel is
 * actually visible (drawer open) — an IntersectionObserver is used rather than checking the drawer's own
 * open state, so this keeps working correctly regardless of *how* the drawer hides itself (display:none,
 * an off-screen transform, etc.). Respects prefers-reduced-motion.
 */
(function () {
  var AUTOPLAY_INTERVAL_MS = 3500;
  var RESUME_AFTER_INTERACTION_MS = 4000;

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  var carouselState = new WeakMap(); // carousel element -> { intervalId, resumeTimeoutId, isVisible, isPaused }

  function getStepWidth(carousel) {
    var firstItem = carousel.querySelector('.cart-drawer__recommendation-item');
    return firstItem ? firstItem.getBoundingClientRect().width : carousel.clientWidth;
  }

  function advance(carousel) {
    var maxScrollLeft = carousel.scrollWidth - carousel.clientWidth;
    if (maxScrollLeft <= 1) return; // nothing to autoplay — every item already fits on screen

    var atEnd = carousel.scrollLeft >= maxScrollLeft - 1;

    carousel.scrollTo({
      left: atEnd ? 0 : carousel.scrollLeft + getStepWidth(carousel),
      behavior: 'smooth',
    });
  }

  function startTimer(carousel) {
    var state = carouselState.get(carousel);
    if (!state || state.isPaused || !state.isVisible || state.intervalId) return;
    state.intervalId = window.setInterval(function () { advance(carousel); }, AUTOPLAY_INTERVAL_MS);
  }

  function stopTimer(carousel) {
    var state = carouselState.get(carousel);
    if (!state || !state.intervalId) return;
    window.clearInterval(state.intervalId);
    state.intervalId = null;
  }

  function pause(carousel) {
    var state = carouselState.get(carousel);
    if (!state) return;

    state.isPaused = true;
    stopTimer(carousel);

    if (state.resumeTimeoutId) window.clearTimeout(state.resumeTimeoutId);
    state.resumeTimeoutId = window.setTimeout(function () {
      state.isPaused = false;
      startTimer(carousel);
    }, RESUME_AFTER_INTERACTION_MS);
  }

  function initCarousel(carousel) {
    if (carouselState.has(carousel)) return;
    carouselState.set(carousel, { intervalId: null, resumeTimeoutId: null, isVisible: false, isPaused: false });

    new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var state = carouselState.get(carousel);
        if (!state) return;
        state.isVisible = entry.isIntersecting;
        if (state.isVisible) {
          startTimer(carousel);
        } else {
          stopTimer(carousel);
        }
      });
    }, { threshold: 0.5 }).observe(carousel);

    carousel.addEventListener('pointerdown', function () { pause(carousel); });
    carousel.addEventListener('touchstart', function () { pause(carousel); }, { passive: true });

    carousel.addEventListener('mouseenter', function () {
      if (!window.matchMedia('(hover: hover)').matches) return;
      var state = carouselState.get(carousel);
      if (state) state.isPaused = true;
      stopTimer(carousel);
    });

    carousel.addEventListener('mouseleave', function () {
      if (!window.matchMedia('(hover: hover)').matches) return;
      var state = carouselState.get(carousel);
      if (state) state.isPaused = false;
      startTimer(carousel);
    });
  }

  function initAllCarousels() {
    document.querySelectorAll('.cart-drawer__recommendations-carousel').forEach(initCarousel);
  }

  // The progress thumb (built earlier) is a separate element layered on top of the carousel — dragging it
  // must pause autoplay too, even though the pointerdown didn't land on the carousel itself.
  document.addEventListener('pointerdown', function (event) {
    var thumb = event.target.closest('.cart-drawer__recommendations-progress-thumb');
    if (!thumb) return;
    var progress = thumb.closest('.cart-drawer__recommendations-progress');
    var carousel = progress && document.getElementById(progress.getAttribute('data-carousel-progress-for'));
    if (carousel) pause(carousel);
  });

  // Re-init after every AJAX cart re-render — same reasoning as the rest of this file: the carousel node
  // is discarded and replaced wholesale, so its old IntersectionObserver/state simply goes away and the
  // new node needs to be picked up fresh.
  var cartDrawer = document.getElementById('cart-drawer');
  if (cartDrawer) {
    new MutationObserver(function () {
      requestAnimationFrame(initAllCarousels);
    }).observe(cartDrawer, { childList: true, subtree: true });
  }

  document.addEventListener('DOMContentLoaded', initAllCarousels);
})();

/**
 * PDP marquee-galleri — kant-til-kant i højre side
 * -------------------------------------------------------------------------------------------------------
 * Galleriet er allerede blødt ud i venstre side via CSS (custom-style.css). Højre side kan ikke løses med
 * CSS alene: hverken margin eller width med !important har nogen effekt på selve galleriets bredde — en
 * anden, mere specifik regel et sted (formentlig en tredjeparts-app) vinder konsekvent. Det samme mønster
 * gælder prisboksen. Direkte inline style via JavaScript med !important virker derimod pålideligt (bevist
 * ved test), så det er metoden her.
 *
 * Prisboksen (.product-info) er bevidst IKKE rørt — den er designet til at flyde ovenpå galleriet
 * (bekræftet visuelt: billeder kigger frem omkring boksen), så den skal ikke selv bløde ud eller flyttes.
 *
 * Thumbnail-rækken under billederne får sin bredde justeret tilbage til den oprindelige (u-bløde) bredde,
 * så den ikke strækkes skævt sammen med galleriets nye, bredere højre kant.
 */
(function () {
  function extendGalleryToRightEdge(gallery) {
    var currentWidth = gallery.getBoundingClientRect().width;
    var gap = window.innerWidth - gallery.getBoundingClientRect().right;
    if (gap <= 0) return;
    gallery.style.setProperty('width', (currentWidth + gap) + 'px', 'important');
  }

  function restoreThumbsNavWidth(gallery, product) {
    var thumbs = gallery.querySelector('.product-marquee-thumbs-nav');
    if (!thumbs) return;
    var productRect = product.getBoundingClientRect();
    var galleryRect = gallery.getBoundingClientRect();
    thumbs.style.setProperty('width', productRect.width + 'px', 'important');
    thumbs.style.setProperty('margin-left', (productRect.left - galleryRect.left) + 'px', 'important');
  }

  function fixGalleryRightEdge(gallery) {
    var product = gallery.closest('.product');
    if (!product) return;
    extendGalleryToRightEdge(gallery);
    restoreThumbsNavWidth(gallery, product);
  }

  function fixAllGalleries() {
    document.querySelectorAll('.product-gallery--marquee').forEach(fixGalleryRightEdge);
  }

  var rightEdgeResizeTimeout = null;
  window.addEventListener('resize', function () {
    window.clearTimeout(rightEdgeResizeTimeout);
    rightEdgeResizeTimeout = window.setTimeout(fixAllGalleries, 150);
  });

  document.addEventListener('DOMContentLoaded', fixAllGalleries);

  var mainEl = document.querySelector('main');
  if (mainEl && window.MutationObserver) {
    new MutationObserver(function () {
      requestAnimationFrame(fixAllGalleries);
    }).observe(mainEl, { childList: true, subtree: true });
  }

  fixAllGalleries();
})();

/**
 * PDP marquee-galleri — korrekt billedbredde uden hvidt/gråt mellemrum
 * -------------------------------------------------------------------------------------------------------
 * sections/main-product.liquid hårdkoder hvert billede til "width: 40vw" (en fast viewport-bredde). Det
 * er uafhængigt af selve galleri-kolonnens faktiske bredde i to-kolonne-gridet, og forskellen mellem de to
 * viser sig som tomt rum. Et CSS-only forsøg på at rette det med "width: 50%" virkede ikke pålideligt:
 * billed-beholderen (.product-marquee__track) har "width: max-content" — den tilpasser sig sine egne
 * børn — og procent-baserede bredder kan ikke regnes ud korrekt i en beholder der selv er
 * størrelses-bestemt af sine børn. Denne kode måler i stedet den faktiske piksel-bredde af galleriet med
 * JavaScript, og sætter en præcis pixel-bredde på hvert billede — det omgår CSS-begrænsningen helt.
 */
(function () {
  function getVisibleItemCount() {
    return window.matchMedia('(min-width: 1000px)').matches ? 2 : 1;
  }

  function sizeMarqueeItems(gallery) {
    var marquee = gallery.querySelector('.product-marquee');
    var items = gallery.querySelectorAll('.product-marquee__item');
    if (!marquee || !items.length) return;

    var marqueeWidth = marquee.getBoundingClientRect().width;
    if (marqueeWidth <= 0) return; // gallery not visible/laid out yet — try again on the next pass

    var itemWidth = marqueeWidth / getVisibleItemCount();

    items.forEach(function (item) {
      item.style.setProperty('width', itemWidth + 'px', 'important');
      item.style.setProperty('flex', '0 0 ' + itemWidth + 'px', 'important');
    });
  }

  function sizeAllGalleries() {
    document.querySelectorAll('.product-gallery--marquee').forEach(sizeMarqueeItems);
  }

  var marqueeResizeTimeout = null;
  window.addEventListener('resize', function () {
    window.clearTimeout(marqueeResizeTimeout);
    marqueeResizeTimeout = window.setTimeout(sizeAllGalleries, 150);
  });

  document.addEventListener('DOMContentLoaded', sizeAllGalleries);

  var mainEl = document.querySelector('main');
  if (mainEl && window.MutationObserver) {
    new MutationObserver(function () {
      requestAnimationFrame(sizeAllGalleries);
    }).observe(mainEl, { childList: true, subtree: true });
  }

  sizeAllGalleries();
})();